/**
 * Usage analytics — fire-and-forget reporter.
 *
 * Sends page views and explicit user actions to the corporate
 * collector at the SynergyApi usage endpoint. The function never
 * throws, never awaits, and never blocks the caller's flow.
 *
 * Convention:
 *   - `page:<Name>` for view events (router.afterEach)
 *   - `btn:<Name>` for click events
 *   - `form:<Name>` for submit events
 *   - `nav:<Name>` for navigation events
 *
 * User identity is read lazily from the auth store. If Pinia is
 * not yet installed (early call from a router hook), we fall back
 * to a null user_id without throwing.
 */
import { useAuthStore } from '@/stores/authStore'

export const APP_KEY = 'lyt-accounting-1779792165986'
export const USAGE_URL = 'https://lna.pos.co.zm/SynergyApi/api/v1/usage/record'
const DEVICE_ID_KEY = 'chrilan_device_id'

export interface TrackOptions {
  /**
   * Mirror to the spec's "view" / "click" / "submit" verbs.
   * Defaults to "view" for backwards compatibility with the
   * router.afterEach page-view call site.
   */
  action?: string
  /**
   * When true, the fetch uses `keepalive: true` so the request
   * survives a fast navigation or tab close. Set this for page
   * views and any event fired on teardown.
   */
  keepalive?: boolean
  /**
   * Free-form contextual fields (provider id, store id, etc.).
   * These are merged into the payload AFTER the canonical keys,
   * so they cannot override app_key / feature / action.
   */
  extra?: Record<string, unknown>
}

/**
 * Stable per-browser device id. Generated on first call and
 * persisted in localStorage. Used to correlate anonymous
 * pre-login events with post-login events.
 */
export function getOrCreateDeviceId(): string {
  if (typeof localStorage === 'undefined') return 'dev_ssr'
  const existing = localStorage.getItem(DEVICE_ID_KEY)
  if (existing) return existing
  const id = 'dev_' + Math.random().toString(36).slice(2, 15) + Date.now().toString(36)
  try {
    localStorage.setItem(DEVICE_ID_KEY, id)
  } catch {
    // localStorage may throw in private mode; fall through.
  }
  return id
}

/**
 * Send a single analytics event. Fire-and-forget.
 *
 * Call shapes:
 *   track(feature)                                    // action='view', no extras
 *   track(feature, action)                            // 'click' | 'submit' | ...
 *   track(feature, action, { ...context, keepalive }) // action + extra bag
 *   track(feature, { action, keepalive, extra })      // options form
 */
export function track(
  feature: string,
  actionOrOptions?: string | TrackOptions,
  extra?: Record<string, unknown>,
): void {
  let action = 'view'
  let keepalive = false
  let mergedExtra: Record<string, unknown> | undefined = extra

  if (typeof actionOrOptions === 'string') {
    action = actionOrOptions
  } else if (actionOrOptions && typeof actionOrOptions === 'object') {
    action = actionOrOptions.action ?? 'view'
    keepalive = actionOrOptions.keepalive === true
    mergedExtra = actionOrOptions.extra ?? extra
  }

  // A caller's positional `extra` bag may also carry a `keepalive`
  // flag (so action-tracking call sites can opt in without using
  // the options-object form). Pull it out before spreading.
  if (mergedExtra && Object.prototype.hasOwnProperty.call(mergedExtra, 'keepalive')) {
    keepalive = keepalive || mergedExtra.keepalive === true
    const next: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(mergedExtra)) {
      if (k !== 'keepalive') next[k] = v
    }
    mergedExtra = next
  }

  // Read auth lazily. If Pinia has not been installed yet (router
  // hook firing before app.use(pinia)) we fall back to null.
  const user_id = (() => {
    try {
      return useAuthStore().user?.id ?? null
    } catch {
      return null
    }
  })()

  const payload = {
    app_key: APP_KEY,
    feature,
    action,
    user_id,
    device_id: getOrCreateDeviceId(),
    ...(mergedExtra ?? {}),
  }

  try {
    void fetch(USAGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive,
    }).catch((err) => {
      console.warn('[UsageTracker] failed', err)
    })
  } catch (err) {
    console.warn('[UsageTracker] failed', err)
  }
}
